// ABOUTME: Wraps the tree selector inside a controlled autocomplete field.
// ABOUTME: Keeps pills, tree state, and selected leaf-node IDs synchronized.

import {ControlledAutocompleteSelect, Pill} from '@hm/fundamental'
import {CircularProgress, FormControl, InputLabel} from '@material-ui/core'
import {makeStyles} from '@material-ui/core/styles'
import {useMemo} from 'react'
import {ControllerRenderProps} from 'react-hook-form'

import {
  clearCheckboxTreeSelection,
  findNodeInTree,
  flattenTreeNodesToAutocompleteOptions,
  getCheckedLeafNodeIds,
  getCheckedNodeIds,
  handleCheckboxTreeChange,
  getTopLevelSelectedNodes
} from '../../../common/utils/utils'
import {CheckboxTreeNode} from '../../../common/types/tree'
import {useTranslation} from '../../../hooks/useTranslation'
import {GroupedRoleAssignment} from '../../../modules/ManageUsers.selectors'

import {TreeSelect} from './TreeSelect'

const useStyles = makeStyles((theme) => ({
  multiSelect: {
    paddingTop: theme.spacing(2.5)
  }
}))

export type TreeLookupFieldProps = {
  field: Pick<
    ControllerRenderProps<GroupedRoleAssignment, keyof GroupedRoleAssignment>,
    'value'
  >
  onSelectedNodeIdsChange: (selectedNodeIds: (string | number)[]) => void
  dataScopeName: string
  errorMessage: string
  treeData: CheckboxTreeNode[]
  setTreeData: (data: CheckboxTreeNode[]) => void
  searchValue: string
  setSearchValue: (value: string) => void
  isLoading: boolean
  isError: boolean
}

export const TreeLookupField = ({
  field: {value},
  onSelectedNodeIdsChange,
  dataScopeName,
  errorMessage,
  treeData,
  setTreeData,
  searchValue,
  setSearchValue,
  isLoading,
  isError
}: TreeLookupFieldProps) => {
  const {t} = useTranslation()
  const classes = useStyles()

  const selectedNodeIds: (string | number)[] = Array.isArray(value) ? value : []
  const options = useMemo(() => flattenTreeNodesToAutocompleteOptions(treeData), [treeData])
  // Leaf IDs for autocomplete display — options are leaf-only so selectedValues must match
  const displaySelectedIds = useMemo(() => getCheckedLeafNodeIds(treeData), [treeData])

  const handleOnAutocompleteChange = (newSelectedNodeIds: (string | number)[]) => {
    const allSelectedCleared = newSelectedNodeIds.length === 0 && displaySelectedIds.length > 0
    if (allSelectedCleared) {
      const updatedTreeData = clearCheckboxTreeSelection(treeData)
      setTreeData(updatedTreeData)
      onSelectedNodeIdsChange(getCheckedNodeIds(updatedTreeData))
      return
    }

    const added = newSelectedNodeIds.filter((id) => !displaySelectedIds.includes(id))
    const removed = displaySelectedIds.filter((id) => !newSelectedNodeIds.includes(id))
    const targetNodeId = added.length > 0 ? added[0] : removed[0]

    const targetNode = findNodeInTree(treeData, targetNodeId)
    if (!targetNode || targetNode.isDisabled) {
      return
    }

    const updatedTreeData = handleCheckboxTreeChange(
      treeData,
      targetNode.id,
      !targetNode.isSelected
    )
    setTreeData(updatedTreeData)
    onSelectedNodeIdsChange(getCheckedNodeIds(updatedTreeData))
  }

  const renderPills = (_selectedValues: (string | number)[], dataId: string) => {
    const selectedNodes = getTopLevelSelectedNodes(treeData)
    if (selectedNodes.length === 0) {
      return null
    }

    return selectedNodes.map((node) => (
      <Pill
        key={node.id}
        text={node.label}
        type="neutral"
        size="regular"
        variant="shade"
        className="mr-2"
        data-cy={`${dataId}-pills`}
      />
    ))
  }

  const showMenuAsTree = searchValue.trim().length === 0

  if (isLoading) {
    return (
      <FormControl key={dataScopeName} margin="normal" className={classes.multiSelect} fullWidth>
        <InputLabel shrink variant="standard">
          {t(`roleAssignment.dataScope.${dataScopeName}`)}
        </InputLabel>
        <CircularProgress size={20} />
      </FormControl>
    )
  }

  if (isError) {
    return (
      <FormControl key={dataScopeName} margin="normal" className={classes.multiSelect} fullWidth>
        <InputLabel shrink variant="standard">
          {t(`roleAssignment.dataScope.${dataScopeName}`)}
        </InputLabel>
        <div>{t('roleAssignment.labelFetchError')}</div>
      </FormControl>
    )
  }

  return (
    <div className="max-w-[600px]">
      <ControlledAutocompleteSelect
        showPills
        isMultiSelect
        options={options}
        selectedValues={displaySelectedIds}
        onChange={handleOnAutocompleteChange}
        onFilterChange={setSearchValue}
        label={t(`roleAssignment.dataScope.${dataScopeName}`)}
        renderPills={renderPills}
        customSelectionComponent={
          showMenuAsTree ? (
            <TreeSelect
              treeData={treeData}
              onTreeDataChange={setTreeData}
              onSelectedNodeIdsChange={onSelectedNodeIdsChange}
            />
          ) : undefined
        }
        error={errorMessage}
        isTouched={true}
        data-cy={`autocomplete-${dataScopeName}`}
      />
    </div>
  )
}
